/**
 * SIMS-8783 — the Cups tab's pure list behaviour, kept out of the component so the ACs can be
 * tested without a DOM.
 *
 * AC5 is the reason several of these exist at all: the list reports how many analytes a cup
 * CARRIES and never describes it as a panel of any size. A cup sold as a "12 Panel" may carry more
 * once validity checks are included, so the product name cannot stand in for the count (FR-44).
 */
import { IAnalyte } from "../../domain/analyte.interface";
import { CupResponse, IAnalyteInCup } from "../../domain/cup.interface";

/** One analyte on a cup, whichever shape the payload used. */
export interface CupAnalyte {
	_id: string;
	shortname: string;
	scientificName: string;
}

/**
 * AC5 — the count column's header. A literal, and deliberately not the design's
 * `{{ countColumnHeader }}` binding: that binding resolves to "Panel" under the prototype's
 * before/after demo flag (markup 1300), which is scaffolding rather than product behaviour.
 */
export const CUP_COUNT_COLUMN_HEADER = "Analytes";

/** AC5 — the chip column's header, the "after" value of the same binding (markup 1301). */
export const CUP_CHIP_COLUMN_HEADER = "Analyte codes";

/**
 * The analytes a cup carries, NORMALISED.
 *
 * `ICup.analytes` is typed `string[] | IAnalyteInCup[]`, and `cup.service.ts` returns the payload
 * with no transform — so `CupResponse`'s narrowing to objects is an unchecked assertion, not a
 * guarantee. The screen this one replaces handled both shapes explicitly (Cups/index.tsx:149-151
 * and :384-390); dropping that would degrade SILENTLY on an id-shaped payload: blank chips, a
 * search that matches no analyte, and "In cups" reading 0 for everything.
 *
 * A string entry is an analyte id. With the `catalogue` it resolves to the real names; without it
 * the id stands in for the shortname, so the row still shows something traceable rather than a
 * blank chip. A payload with no array at all yields an empty list.
 */
export const cupAnalytes = (
	cup: CupResponse,
	catalogue?: Map<string, IAnalyte>,
): CupAnalyte[] => {
	const raw = Array.isArray(cup.analytes)
		? (cup.analytes as (string | IAnalyteInCup)[])
		: [];
	return raw.map((entry) => {
		if (typeof entry !== "string") {
			return {
				_id: entry._id,
				shortname: entry.shortname,
				scientificName: entry.scientificName,
			};
		}
		const known = catalogue?.get(entry);
		return {
			_id: entry,
			shortname: known?.shortname ?? entry,
			scientificName: known?.scientificName ?? "",
		};
	});
};

const analytesOf = cupAnalytes;

/** AC5 — how many analytes this cup carries. A count, never a panel size. */
export const analyteCount = (cup: CupResponse): number =>
	analytesOf(cup).length;

/** AC5 — the codes rendered as chips, which are also what AC3's analyte search matches. */
export const analyteCodes = (
	cup: CupResponse,
	catalogue?: Map<string, IAnalyte>,
): string[] => cupAnalytes(cup, catalogue).map((a) => a.shortname);

/**
 * AC3 — every field a cup can be found by.
 *
 * FOUR fields, not the three AC3 lists. The Hi-Fi matches name, vendor, description and analyte
 * (markup 1072-1073) and its no-results copy promises exactly that to the user. Raised as Q1 on
 * the ticket; if the PO narrows AC3 instead, remove the description line.
 *
 * The analyte arm matches both the shortname and the scientific name, so "THC" and
 * "Tetrahydrocannabinol" both find the same cup.
 */
export const searchableCupFields = (
	cup: CupResponse,
	catalogue?: Map<string, IAnalyte>,
): (string | undefined | null)[] => [
	cup.name,
	cup.vendor,
	cup.description,
	...cupAnalytes(cup, catalogue).flatMap((a) => [
		a.shortname,
		a.scientificName,
	]),
];

/** The catalogue as `cupAnalytes` wants it, built once per analytes change. */
export const analyteCatalogue = (analytes: IAnalyte[]): Map<string, IAnalyte> =>
	new Map(analytes.map((a) => [a._id, a]));

/**
 * AC4 — the single filter dimension for the Cups tab.
 *
 * An empty vendor yields no category, so a cup missing its vendor cannot create a blank filter
 * chip that matches nothing.
 */
export const cupVendorCategories = (cup: CupResponse): string[] =>
	cup.vendor ? [cup.vendor] : [];

/**
 * AC4 — the vendor chips, DERIVED from the data rather than declared.
 *
 * The prototype hard-codes its analyte type list (markup 1007-1009) while deriving vendors; AC4
 * asks for both dimensions to behave "in the same way", so both are derived. See Q2.
 */
export const vendorFilterOptions = (cups: CupResponse[]): string[] =>
	Array.from(new Set(cups.map((c) => c.vendor).filter(Boolean))).sort((a, b) =>
		a.localeCompare(b, undefined, { sensitivity: "base" }),
	);

/** Sort keys the Cups table offers. */
export const cupSortValue = (cup: CupResponse, key: string): unknown => {
	switch (key) {
		case "vendor":
			return cup.vendor;
		case "status":
			return cup.status;
		case "analyteCount":
			return analyteCount(cup);
		case "name":
		default:
			return cup.name;
	}
};

/**
 * The three distinct list states the hand-off requires to be visibly different (§7.1) plus the
 * load failure. Copy is ours to write, and deliberately avoids the word "panel" (AC5, Q3).
 */
export const cupsStateCopy = {
	loadFailedTitle: "Cups couldn't be loaded",
	loadFailedBody:
		"The request failed before anything was returned. Nothing has been changed.",
	emptyTitle: "No cups yet",
	emptyBody:
		"A cup is the set of analytes a provider screens for when recording a visit. Add the first cup to start recording visits.",
	emptyIcon: "water_drop",
	loadingLabel: "Loading cups…",
	noResultsTitle: (query: string) => `No cups match “${query}”`,
	noResultsBody:
		"No cup name, vendor, description or analyte contains that text with the current vendor filter.",
	addLabel: "Create cup",
};
